import { FC, memo, useRef } from 'react';

import { generateUid } from '@/shared/lib/helpers/generate-uid.helper';

import { useModalActions } from '../model/slice/modal.slice';
import { CreateModalHookConfig, ModalOptions, ModalVariant } from '../model/types/modal.interface';

type CreateModalOptions<T extends ModalOptions> = Omit<T, keyof ModalOptions> & {
	onClose?: () => void;
	isOverlayClose?: boolean;
	variant?: ModalVariant;
};

export const createModalHook = <T extends ModalOptions>(
	component: (props: T) => FC<unknown>,
	config?: CreateModalHookConfig,
) =>
	function useModal(): [(props?: CreateModalOptions<T>) => void, () => void] {
		const { removeModal, addModal } = useModalActions();
		const idRef = useRef<string>(undefined);

		const hideModal = () => {
			if (idRef.current) {
				removeModal(idRef.current);
			}
		};

		const createModal = (props: CreateModalOptions<T> = {} as T) => {
			const id = generateUid();

			idRef.current = addModal({
				id,
				component: memo(
					component({
						...props,
						onClose: props?.onClose || hideModal,
					} as T),
				),
				isOverlayClose: props?.isOverlayClose ?? true,
				variant: props?.variant ?? config?.variant ?? 'center',
			}).payload.id;
		};

		return [createModal, hideModal];
	};
